import type { ReactNode } from "react";

import { createContext, useContext } from "react";
import type {
  AppBranding,
  FrontendProvision,
  LicenseReport,
} from "./FrontendProvision";

const FrontendProvisionContext = createContext<FrontendProvision | null>(null);

export const useFrontendProvision = (): FrontendProvision => {
  const provision = useContext(FrontendProvisionContext);
  if (!provision) throw new Error("FrontendProvisionProvider not found");
  return provision;
};

/** The provision, or `undefined` outside a `FrontendProvisionProvider`. */
export const useOptionalFrontendProvision = (): FrontendProvision | undefined =>
  useContext(FrontendProvisionContext) ?? undefined;

export const useBranding = (): AppBranding => {
  return useFrontendProvision().branding;
};

/**
 * Returns the bundled markdown changelog (from a repo-root `CHANGELOG.md`), or
 * `undefined` when the project has none. Used by the generic About dialog to
 * conditionally show a changelog tab.
 */
export const useChangelog = (): string | undefined => {
  return useFrontendProvision().changelog;
};

/**
 * Returns a lazy loader for the workspace license inventory, or `undefined`
 * when none was bundled. The generic About dialog uses its presence to show a
 * licenses tab and calls it to load the (large) data on demand.
 */
export const useLicensesLoader = ():
  | (() => Promise<LicenseReport>)
  | undefined => {
  return useFrontendProvision().licensesLoader;
};

export const FrontendProvisionProvider = ({
  children,
  provision,
}: {
  children: ReactNode;
  provision: FrontendProvision;
}) => {
  return (
    <FrontendProvisionContext.Provider value={provision}>
      {children}
    </FrontendProvisionContext.Provider>
  );
};
